import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {dateComparator} from "../../../../../../../core/services/utils/utils";
import {useBizFormApiResolver} from "../../../../../../hooks/use-biz-form-api-resolver";
import {Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineOppositeContent, TimelineSeparator} from "@mui/lab";
import DateUtils from "../../../../../../../core/services/date-utils";
import {Box, Card, SvgIcon, Typography} from "@mui/material";
import {Edit} from "@mui/icons-material";
import {Col, Collapse, Row} from "reactstrap";
import {ReactComponent as ToggleIcon2} from "../../../../../../../assets/images/warranty-statuses-reasons/toggle.svg";
import classnames from "classnames";
import IconButton from "../../../../../../components/base/button/icon_button";
import {QuestionAnswer} from "../../../../../../components/dialogs/view-form-response/question-answer-section";


const NCRSingleStatuses = ({statuses, createdAt, isSummaryCardToggled, updateQuestions}) => {
    const cache = useRef({form: {}, formResponse: {}});
    const [openedStatuses, setOpenedStatuses] = useState({});
    const [loading, setLoading] = useState({});
    const [openedStatusesQuestionsAndAnswers, setOpenedStatusesQuestionsAndAnswers] = useState({});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    const ncrBizForm = useBizFormApiResolver((t) => t.ncrService);

    const currentStatus = useMemo(() => statuses?.find(e => e.isCurrentStatus), [statuses]);
    const sortedStatuses = useMemo(() => {
        const sortedStatuses = [...(statuses ?? [])];
        sortedStatuses.sort((a, b) => dateComparator(b.submitDateTime, a.submitDateTime));
        return sortedStatuses;
    }, [statuses]);

    /**
     * Fetches the questions and answers for the report from the server and sets the questionsAndAnswers state.
     * @type {(function(any): Promise<void>)}
     */
    const getQuestionsAndAnswers = useCallback(async (status) => {
        const {formReference, formResponseReference} = status;
        setLoading(prevState => ({...prevState, [status.submitDateTime]: true}));
        const [formResponse, formResponseResponse] = await Promise.all([
            formReference && !cache.current.form[formReference] ? ncrBizForm.getForm({id: formReference}) : !cache.current.form[formReference] ? Promise.resolve(null) : Promise.resolve(cache.current.form[formReference]),
            formResponseReference && !cache.current.formResponse[formResponseReference] ? ncrBizForm.getFormResponse({id: formResponseReference}) : !cache.current.formResponse[formResponseReference] ? Promise.resolve(null) : Promise.resolve(cache.current.formResponse[formResponseReference]),
        ])
        if (!isMounted())
            return;
        setLoading(prevState => ({...prevState, [status.submitDateTime]: false}));
        if (formResponse?.resultFlag) {
            cache.current.form[formReference] = formResponse;
        }
        if (formResponseResponse?.resultFlag) {
            cache.current.formResponse[formResponseReference] = formResponseResponse;
        }
        const questions = formResponse?.data?.questions ?? [];
        const answers = formResponseResponse?.data?.questionResponses ?? [];
        if (formResponse?.resultFlag) {
            setOpenedStatusesQuestionsAndAnswers(prevState => ({
                ...prevState,
                [status.submitDateTime]: questions.map((question) => {
                    const answer = answers.find((answer) => answer.questionId === question.id);
                    return {question: question, answer: answer}
                })
            }))
        }
    }, [isMounted, ncrBizForm])

    /**
     * Fetches the questions and answers for the current status from the server and sets the questionsAndAnswers state.
     */
    useEffect(() => {
        if (currentStatus) {
            setOpenedStatuses(prevState => ({...prevState, [currentStatus.submitDateTime]: true}));
            void getQuestionsAndAnswers(currentStatus);
        }
    }, [currentStatus, getQuestionsAndAnswers])


    /**
     * Toggles the questionnaire of the given
     * @type {function(*): void}
     */
    const toggleQuestionnaire = useCallback((status) =>
        setOpenedStatuses(prevState => {
            const found = prevState[status.submitDateTime]
            if (!found) {
                void getQuestionsAndAnswers(status);
            }
            return {
                ...prevState,
                [status.submitDateTime]: !found
            };
        }), [getQuestionsAndAnswers])

    /**
     * Opens the dialog to update the questions of the current status.
     * @type {(function(): void)}
     */
    const openUpdateQuestionsDialog = useCallback(() => {
        updateQuestions((formReference, formResponseReference) => {
            cache.current.form[formReference] = null;
            cache.current.formResponse[formResponseReference] = null;
            void getQuestionsAndAnswers(currentStatus);
        });
    }, [currentStatus, getQuestionsAndAnswers, updateQuestions])

    return (
        <Timeline position={'left'}>
            {
                sortedStatuses.map(status => (
                    <TimelineItem
                        key={status.submitDateTime}
                        position={'right'}
                        sx={{width: '100%', maxWidth: '100%'}}
                    >
                        <TimelineOppositeContent sx={{display: 'none'}}/>
                        <TimelineSeparator>
                            <TimelineDot variant={'outlined'} color={status?.isCurrentStatus ? 'primary' : 'grey'}/>
                            <TimelineConnector sx={{backgroundColor: status?.isCurrentStatus ? 'primary.main' : 'text.disabled'}}/>
                        </TimelineSeparator>
                        <TimelineContent component={'div'} sx={{display: 'flex', flexDirection: 'column', gap: 0}}>
                            <Typography
                                variant={'body2'}
                                fontWeight={600}
                                color={status?.isCurrentStatus ? 'primary.main' : 'text.secondary'}
                            >
                                {status?.statusTitle}
                            </Typography>
                            <Typography
                                variant={'caption'}
                                fontWeight={500}
                                color={status?.isCurrentStatus ? 'primary.main' : 'text.secondary'}
                                sx={{marginBlockEnd: 1}}
                            >
                                {DateUtils.serverToLocal(status?.submitDateTime).format(DateUtils.dateTimeFormat)}
                            </Typography>
                            <Card variant={'outlined'} sx={{padding: 1}}>
                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <Typography
                                        variant={'caption'}
                                        fontWeight={600}
                                        color={!(status?.formResponseReference && status?.formReference) ? 'text.disabled' : 'text.secondary'}
                                    >
                                        {
                                            status?.formResponseReference && status?.formReference &&
                                            "Questionnaire"
                                        }
                                        {
                                            !(status?.formResponseReference && status?.formReference) &&
                                            "This status had no questionnaire with it."
                                        }
                                    </Typography>
                                    {
                                        status?.formResponseReference && status?.formReference &&
                                        <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                                            {
                                                status?.isCurrentStatus && operatorAccessLevels.packages.ncr.manageReport &&
                                                <IconButton
                                                    className={'mx-1'}
                                                    color={'secondary'}
                                                    onPressed={openUpdateQuestionsDialog}
                                                >
                                                    <Edit className={'icon small'}/>
                                                </IconButton>
                                            }
                                            <IconButton
                                                className={'toggle-button'}
                                                onPressed={() => toggleQuestionnaire(status)}
                                            >
                                                <SvgIcon
                                                    component={ToggleIcon2}
                                                    className={classnames('icon small toggle-icon', {'open': openedStatuses[status.submitDateTime]})}
                                                />
                                            </IconButton>
                                        </Box>
                                    }
                                </Box>
                                {
                                    status?.formResponseReference && status?.formReference &&
                                    <Collapse isOpen={openedStatuses[status.submitDateTime]}>
                                        {
                                            !isSummaryCardToggled &&
                                            <div className={'d-flex flex-column mt-3'} style={{gap: 24}}>
                                                {
                                                    loading[status.submitDateTime] &&
                                                    Array.from({length: 4}, ((_, index) => (
                                                        <QuestionAnswer
                                                            key={`loading-${index}`}
                                                            loading
                                                        />
                                                    )))
                                                }
                                                {
                                                    !loading[status.submitDateTime] &&
                                                    openedStatusesQuestionsAndAnswers[status.submitDateTime]?.map(({question, answer}) => (
                                                        <QuestionAnswer
                                                            key={question?.id}
                                                            question={question}
                                                            answer={answer}
                                                        />
                                                    ))
                                                }
                                            </div>
                                        }
                                        {
                                            isSummaryCardToggled &&
                                            <div className={'d-flex flex-column mt-4'}>
                                                <Row noGutters className={'justify-content-center'}>
                                                    <Col xs={12}
                                                         className={'d-flex justify-content-center no-gutters text-md font-weight-semi-bold'}>
                                                        {openedStatusesQuestionsAndAnswers[status.submitDateTime]?.reduce((acc, {answer}) => acc + (answer ? 1 : 0), 0)}
                                                    </Col>
                                                    <Col xs={12} className={'d-flex justify-content-center no-gutters'}>
                                                        <p className={'p-2 text-xs font-weight-semi-bold text-center'}>
                                                            Completed answers
                                                        </p>
                                                    </Col>
                                                </Row>
                                            </div>
                                        }
                                    </Collapse>
                                }
                            </Card>
                        </TimelineContent>
                    </TimelineItem>
                ))
            }
            {
                createdAt &&
                <TimelineItem position={'right'}>
                    <TimelineOppositeContent sx={{display: 'none'}}/>
                    <TimelineSeparator>
                        <TimelineDot variant={'outlined'}/>
                    </TimelineSeparator>
                    <TimelineContent component={'div'} sx={{display: 'flex', flexDirection: 'column'}}>
                        <Typography variant={'body2'} fontWeight={500} color={'text.disabled'}>
                            Created
                        </Typography>
                        <Typography variant={'caption'} fontWeight={500} color={'text.disabled'}>
                            {DateUtils.serverToLocal(createdAt).format(DateUtils.dateTimeFormat)}
                        </Typography>
                    </TimelineContent>
                </TimelineItem>
            }
        </Timeline>
    );
}

export default NCRSingleStatuses;